import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { format, parseISO } from 'date-fns';
import React, { useCallback, useRef, useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { FaPencilAlt, FaTrashAlt } from 'react-icons/fa';
import { IProcedimentoEvolucao } from '..';
import Loading from '../../../../components/Loading';
import TextArea from '../../../../components/TextArea';
import { useToast } from '../../../../hooks/toast';
import api from '../../../../services/api';
import getResponseErrors from '../../../../utils/getResponseErrors';

// import { Container } from './styles';

interface FormDados {
  texto: string;
}

type Props = {
  procedimentoId: number;
  evolucoes?: IProcedimentoEvolucao[];
  medUsuCodigo?: number;
  bloqueado: boolean;
  handleEditouEvolucao(
    evo: IProcedimentoEvolucao,
    operacao: 'add' | 'edit' | 'remove',
  ): void;
};

const Evolucao: React.FC<Props> = ({
  procedimentoId,
  evolucoes,
  medUsuCodigo,
  bloqueado,
  handleEditouEvolucao,
}) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const formRefEvolucao = useRef<FormHandles>(null);
  const [evolucaoSelecionada, setEvolucaoSelecionada] = useState<
    number | undefined
  >(undefined);

  const handleSubmitEvolucao = useCallback(
    async (dados: FormDados) => {
      if (dados.texto === '') {
        addToast({
          title: 'Não foi possível salvar evolução',
          type: 'error',
          description: 'nemhuma texto foi digitado',
        });
        return;
      }
      setLoading(true);
      try {
        if (evolucaoSelecionada) {
          const evSelecionada = evolucoes?.find(
            ev => ev.id === evolucaoSelecionada,
          );
          if (evSelecionada && medUsuCodigo) {
            await api.put(`/tratamento/evolucao/${evolucaoSelecionada}`, {
              ...dados,
              MedUsuCodigo: medUsuCodigo,
              idProcedimento: procedimentoId,
            });
            handleEditouEvolucao(
              {
                ...evSelecionada,
                ...dados,
                MedUsuCodigo: medUsuCodigo,
              },
              'edit',
            );
          }
        } else {
          const responsePost = await api.post<IProcedimentoEvolucao>(
            `/tratamento/evolucao/${procedimentoId}`,
            {
              ...dados,
              MedUsuCodigo: medUsuCodigo,
            },
          );
          handleEditouEvolucao(
            {
              ...responsePost.data,
              dataFormatada: format(
                parseISO(responsePost.data.created_at.toString()),
                'dd/MM/yyyy',
              ),
            },
            'add',
          );
        }
        formRefEvolucao.current?.reset();
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível salvar evolução',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      evolucaoSelecionada,
      procedimentoId,
      addToast,
      evolucoes,
      handleEditouEvolucao,
      medUsuCodigo,
    ],
  );

  const handleEditarEvolucao = useCallback((evo: IProcedimentoEvolucao) => {
    setEvolucaoSelecionada(evo.id);
    formRefEvolucao.current?.setFieldValue('texto', evo.texto);
  }, []);

  const handleRemoveEvolucao = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        const evSelecionada = evolucoes?.find(ev => ev.id === id);
        if (evSelecionada && medUsuCodigo) {
          await api.delete(`/tratamento/evolucao/${id}`);
          handleEditouEvolucao(evSelecionada, 'remove');
        }
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível salvar evolução',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, handleEditouEvolucao, evolucoes, medUsuCodigo],
  );

  return (
    <>
      {loading && <Loading backgroundBlack={false} />}
      {!bloqueado && (
        <>
          <div className="mb-4">
            <Form ref={formRefEvolucao} onSubmit={handleSubmitEvolucao}>
              <TextArea name="texto" label="Descreva a evolução" rows={4} />
              <Button
                type="submit"
                variant="primary mt-2"
                size="sm"
                disabled={loading}
              >
                {loading ? 'Enviando...' : 'Salvar'}
              </Button>
            </Form>
          </div>
        </>
      )}
      <div>
        {evolucoes && evolucoes.length > 0 ? (
          <>
            <Table
              className="table table-hover table-sm"
              id="GrdProcedimentos"
              style={{ borderWidth: '0px', marginTop: '-2px' }}
            >
              <tbody>
                <tr className="bg-light" style={{ fontSize: 'Small' }}>
                  <th scope="col">Evolução</th>
                  <th scope="col">Data</th>
                  <th scope="col">Editar</th>
                  <th scope="col">Excluir</th>
                </tr>
                {evolucoes.map(evo => (
                  <tr key={`evolucao-${evo.id}`} style={{ fontSize: 'Small' }}>
                    <td>{evo.texto}</td>
                    <td style={{ width: '100px' }}>{evo.dataFormatada}</td>
                    <td className="w3-padding-0" style={{ width: '50px' }}>
                      <Button
                        type="button"
                        variant="outline-primary"
                        size="sm"
                        title={evo.id.toString() || ''}
                        onClick={() => handleEditarEvolucao(evo)}
                        disabled={bloqueado}
                      >
                        <FaPencilAlt />
                      </Button>
                    </td>
                    <td className="w3-padding-0" style={{ width: '50px' }}>
                      <Button
                        type="button"
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          handleRemoveEvolucao(evo.id);
                        }}
                        disabled={bloqueado}
                      >
                        <FaTrashAlt />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        ) : (
          <div className="text-center">
            <h4>Nenhuma evolução lançada até o momento.</h4>
          </div>
        )}
      </div>
    </>
  );
};

export default Evolucao;
